(() => {
  const activationClass = 'uscc-collection-card-gallery-enabled';
  const activationEvent = 'uscc:collection-card-gallery:activate';
  const elementName = 'collection-card-gallery';
  const activationGuard = 'usccCollectionCardGalleryActivationBound';

  if (!('customElements' in window)) return;

  class CollectionCardGallery extends HTMLElement {
    connectedCallback() {
      if (document.documentElement.classList.contains(activationClass)) {
        this.activate();
      }
    }

    activate() {
      if (this.hydrated || !document.documentElement.classList.contains(activationClass)) return;

      const template = this.querySelector('[data-uscc-gallery-template]');
      const controlMedia = this.querySelector('[data-uscc-control-media]');
      const status = this.querySelector('[data-uscc-status]');
      const productLink = document.getElementById(this.dataset.usccProductLinkId);
      const pagination = document.getElementById(this.dataset.usccPaginationId);

      if (!template || !controlMedia || !status || !productLink || !pagination) {
        return;
      }

      const fragment = template.content.cloneNode(true);
      const slidesContainer = fragment.querySelector('[data-uscc-slides]');
      const slides = slidesContainer ? Array.from(slidesContainer.querySelectorAll('[data-uscc-slide]')) : [];

      if (!slidesContainer || slides.length < 2) return;

      if (this.dataset.usccReusePrimary === 'true') {
        const controlImage = controlMedia.querySelector('img');
        const templateImage = slides[0].querySelector('img');

        if (controlImage && templateImage) {
          templateImage.replaceWith(controlImage);
        }
      }

      this.appendChild(fragment);
      this.nativeSlider = this.querySelector('[data-uscc-native-slider]');
      this.slidesContainer = this.querySelector('[data-uscc-slides]');
      this.previousButton = this.querySelector('[data-uscc-previous]');
      this.nextButton = this.querySelector('[data-uscc-next]');

      if (
        !this.nativeSlider ||
        !this.slidesContainer ||
        !this.previousButton ||
        !this.nextButton ||
        typeof this.nativeSlider.resetPages !== 'function'
      ) {
        return;
      }

      this.slides = Array.from(this.slidesContainer.querySelectorAll('[data-uscc-slide]'));
      this.status = status;
      this.productLink = productLink;
      this.pagination = pagination;
      this.controlMedia = controlMedia;
      this.currentIndex = 0;
      this.suppressNextClick = false;
      this.lastScrollLeft = 0;

      this.slidesContainer.querySelectorAll('img').forEach((image) => {
        image.draggable = false;
      });

      this.dots = this.slides.map((_, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'collection-card-gallery__dot';
        dot.tabIndex = -1;
        dot.setAttribute('aria-controls', this.dataset.usccStageId);
        dot.addEventListener('click', (event) => this.onDotClick(event, index));
        return dot;
      });
      this.pagination.replaceChildren(...this.dots);

      this.setAttribute('role', 'region');
      this.setAttribute('tabindex', '0');
      this.setAttribute(
        'aria-labelledby',
        `${this.dataset.usccProductLinkId} ${this.dataset.usccGalleryLabelId}`
      );
      this.setAttribute('aria-roledescription', this.dataset.usccSliderName);
      this.previousButton.setAttribute('aria-controls', this.dataset.usccStageId);
      this.nextButton.setAttribute('aria-controls', this.dataset.usccStageId);

      this.nativeSlider.addEventListener('slideChanged', (event) => this.onSlideChanged(event));
      this.slidesContainer.addEventListener('scroll', () => this.onSliderScroll(), { passive: true });
      this.addEventListener('keydown', (event) => this.onKeydown(event));
      this.addEventListener('dragstart', (event) => event.preventDefault());
      this.addEventListener('click', (event) => this.onGalleryClick(event), true);

      this.hydrated = true;
      this.render(false);
      this.controlMedia.hidden = true;
      this.previousButton.hidden = false;
      this.nextButton.hidden = false;
      this.status.hidden = false;
      this.pagination.removeAttribute('aria-hidden');
      this.pagination.hidden = false;
      this.classList.add('collection-card-gallery--hydrated');

      window.requestAnimationFrame(() => {
        this.nativeSlider.resetPages();
        this.render(false);
      });
    }

    onDotClick(event, index) {
      event.preventDefault();
      event.stopPropagation();
      this.show(index);
      event.currentTarget.blur();
    }

    onKeydown(event) {
      if (event.target !== this) return;

      const keyTargets = {
        ArrowLeft: this.currentIndex - 1,
        ArrowRight: this.currentIndex + 1,
        Home: 0,
        End: this.slides.length - 1,
      };

      if (!(event.key in keyTargets)) return;

      event.preventDefault();
      event.stopPropagation();
      this.show(keyTargets[event.key]);
    }

    onSlideChanged(event) {
      const nextIndex = this.slides.indexOf(event.detail.currentElement);
      if (nextIndex < 0 || nextIndex === this.currentIndex) return;

      this.currentIndex = nextIndex;
      this.render(true);
    }

    onSliderScroll() {
      if (Math.abs(this.slidesContainer.scrollLeft - this.lastScrollLeft) < 2) return;

      this.lastScrollLeft = this.slidesContainer.scrollLeft;
      this.suppressNextClick = true;
      window.clearTimeout(this.clickSuppressionTimer);
      this.clickSuppressionTimer = window.setTimeout(() => {
        this.suppressNextClick = false;
      }, 500);
    }

    onGalleryClick(event) {
      if (event.target.closest?.('[data-uscc-previous], [data-uscc-next]')) return;

      const isGalleryStageClick = event.target === this || event.target.closest?.('[data-uscc-slides]');
      if (!isGalleryStageClick) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      if (this.suppressNextClick) {
        this.suppressNextClick = false;
        return;
      }

      this.productLink.click();
    }

    show(index) {
      const nextIndex = Math.max(0, Math.min(index, this.slides.length - 1));
      if (nextIndex === this.currentIndex) return;

      this.nativeSlider.initPages();
      const slideOffset = this.nativeSlider.sliderItemOffset || this.slidesContainer.clientWidth;
      this.nativeSlider.setSlidePosition(nextIndex * slideOffset);
    }

    render(announce) {
      this.slides.forEach((slide, index) => {
        const isActive = index === this.currentIndex;
        slide.toggleAttribute('aria-hidden', !isActive);
      });

      this.renderControls(announce);
    }

    renderControls(announce) {
      const current = String(this.currentIndex + 1);
      const total = String(this.slides.length);
      const availability = this.dataset.usccImageAvailable.replace('__index__', current);
      const statusText = `${availability} ${current} ${this.dataset.usccOf} ${total}`;

      this.dots.forEach((dot, index) => {
        const isActive = index === this.currentIndex;
        const distanceFromActive = Math.abs(index - this.currentIndex);
        const dotIndex = String(index + 1);
        const dotAvailability = this.dataset.usccImageAvailable.replace('__index__', dotIndex);

        dot.classList.toggle('collection-card-gallery__dot--active', isActive);
        dot.classList.toggle(
          'collection-card-gallery__dot--near',
          distanceFromActive > 0 && distanceFromActive <= 2
        );
        dot.classList.toggle('collection-card-gallery__dot--far', distanceFromActive === 3);
        dot.classList.toggle('collection-card-gallery__dot--edge', distanceFromActive >= 4);
        if (isActive) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
        dot.setAttribute('aria-label', `${dotAvailability} ${dotIndex} ${this.dataset.usccOf} ${total}`);
      });

      this.previousButton.disabled = this.currentIndex === 0;
      this.nextButton.disabled = this.currentIndex === this.slides.length - 1;

      if (announce) {
        this.status.textContent = '';
        window.requestAnimationFrame(() => {
          this.status.textContent = statusText;
        });
      } else {
        this.status.textContent = statusText;
      }
    }
  }

  if (!customElements.get(elementName)) {
    customElements.define(elementName, CollectionCardGallery);
  }

  if (!window[activationGuard]) {
    document.addEventListener(activationEvent, () => {
      document.querySelectorAll(elementName).forEach((gallery) => gallery.activate());
    });
    window[activationGuard] = true;
  }
})();
